import { Box, Button, Chip, IconButton, Snackbar, TextField, Typography, useTheme } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import EditIcon from '@mui/icons-material/Edit';
import Header from '../../components/Header';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { can, filterActionColumns } from '../../utils/permissions';

const Feedback = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const token = localStorage.getItem('token');

  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const getData = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(pageSize));
    if (search) params.set('search', search);
    const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/feedback?${params.toString()}`, { headers: { token } });
    const data = await res.json();
    if (data.success) {
      setFeedback(data.feedbackQuestions);
      setTotalCount(data.pagination?.total ?? data.feedbackQuestions.length);
    }
    setLoading(false);
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search]);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleDelete = async (id) => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/feedback/${id}`, {
      method: 'DELETE',
      headers: { token },
    });
    const data = await res.json();
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
      if (data.success) {
        window.location.reload();
      }
    }
  };

  const openDeleteDialog = (id) => {
    setIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setIdToDelete(null);
  };

  const confirmDelete = async () => {
    if (idToDelete == null) return;
    await handleDelete(idToDelete);
    closeDeleteDialog();
  };

  const columns = [
    {
      field: 'question',
      headerName: 'Question',
      flex: 1,
      minWidth: 150,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'tooltip',
      headerName: 'Tooltip',
      flex: 1,
      minWidth: 150,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'questionType',
      headerName: 'Question Type',
      flex: 1,
      minWidth: 150,
      cellClassName: 'name-column--cell',
      renderCell: (params) => {
        let status;
        switch (String(params.value)) {
          case '1': status = 'Single Choice'; break;
          case '4': status = 'Multiple Choice'; break;
          case '2': status = 'Short Answer'; break;
          case '3': status = 'Rating'; break;
          default: status = 'Unknown';
        }
        return <span>{status}</span>;
      },
    },
    {
      field: 'country',
      headerName: 'Destination',
      flex: 1,
      minWidth: 150,
      cellClassName: 'name-column--cell',
      renderCell: (params) => {
        if (!params.value || params.value === '-') {
          return (
            <Chip
              icon={<PublicIcon style={{ fontSize: 14 }} />}
              label="GLOBAL"
              size="small"
              sx={{ backgroundColor: '#1a6b3c', color: '#fff', fontWeight: 700, fontSize: 11 }}
            />
          );
        }
        const depDate = params.row.departureDate;
        if (depDate) {
          const dateOnly = String(depDate).split('T')[0];
          return (
            <Box>
              <span>{params.value}</span>
              <Chip
                label={dateOnly}
                size="small"
                sx={{ ml: 0.8, backgroundColor: '#1a3b6b', color: '#fff', fontSize: 10 }}
              />
            </Box>
          );
        }
        return <span>{params.value}</span>;
      },
    },
    {
      field: 'Edit',
      filterable: false,
      renderCell: (cellValues) => (
        <Link to={`/feedback/${cellValues.row.id}`}>
          <Button
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.greenAccent[700] },
            }}
            variant="contained"
          >
            <EditIcon />
          </Button>
        </Link>
      ),
    },
    {
      field: 'Delete',
      filterable: false,
      renderCell: (cellValues) => (
        <Button
          sx={{
            backgroundColor: colors.redAccent[500],
            color: colors.grey[100],
            ':hover': { backgroundColor: colors.redAccent[600] },
          }}
          variant="contained"
          onClick={() => openDeleteDialog(cellValues.row.id)}
        >
          <DeleteIcon />
        </Button>
      ),
    },
  ];

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenToast(false);
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap">
        <Header title="FEEDBACK QUESTIONNAIRE" subtitle="All Feedback Questions" />

        <Box display="flex" gap={1}>
          {can('surveys.edit') && (
            <Link to={'/feedback/reorder'}>
              <Button
                sx={{
                  backgroundColor: colors.greenAccent[600],
                  color: colors.grey[100],
                  fontSize: '14px',
                  fontWeight: 'bold',
                  padding: '10px 20px',
                  '&:hover': { backgroundColor: colors.greenAccent[700] },
                }}
              >
                <SwapVertIcon sx={{ mr: '8px' }} />
                Reorder
              </Button>
            </Link>
          )}
          {can('surveys.create') && (
            <Link to={'/feedback/addfeedback'}>
              <Button
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: colors.grey[100],
                  fontSize: '14px',
                  fontWeight: 'bold',
                  padding: '10px 20px',
                  '&:hover': { backgroundColor: colors.blueAccent[800] },
                }}
              >
                <HelpOutlineIcon sx={{ mr: '10px' }} />
                Add Feedback
              </Button>
            </Link>
          )}
        </Box>
      </Box>

      <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center" m="10px 0 0 0">
        <TextField
          size="small"
          placeholder="Search by question, tooltip, destination..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: colors.grey[300] }} />,
          }}
          sx={{ minWidth: '320px', backgroundColor: colors.primary[400] }}
        />
      </Box>

      {search && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 500 }}>
            Active filters:
          </Typography>
          <Chip
            label={`Search: ${search}`}
            size="small"
            onDelete={handleClearSearch}
            sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
          />
          {!loading && (
            <Typography variant="body2" sx={{ color: colors.greenAccent[400], ml: 1 }}>
              {totalCount} result{totalCount !== 1 ? 's' : ''}
            </Typography>
          )}
          <Typography
            variant="body2"
            onClick={handleClearSearch}
            sx={{
              color: colors.redAccent[500],
              cursor: 'pointer',
              ml: 1,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear all
          </Typography>
        </Box>
      )}

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .name-column--cell': { color: colors.grey[100] },
          '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.blueAccent[700], borderBottom: 'none' },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': { borderTop: 'none', backgroundColor: colors.blueAccent[700] },
          '& .MuiCheckbox-root': { color: `${colors.greenAccent[200]} !important` },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': { color: `${colors.grey[100]} !important`, fontSize: 14 },
        }}
      >
        <DataGrid
          rows={feedback}
          columns={filterActionColumns(columns, { Edit: 'surveys.edit', Delete: 'surveys.delete' })}
          getRowId={(row) => row.id}
          disableColumnFilter
          loading={loading}
          paginationMode="server"
          page={page - 1}
          pageSize={pageSize}
          rowCount={totalCount}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPage(1);
          }}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        title="Delete feedback question?"
        message="Are you sure you want to delete this feedback question?"
        colors={colors}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Feedback;
